import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  provideHttpClient, withInterceptorsFromDi, withXhr, HttpClient,
} from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
// Legacy animations engine required by @truenas/ui-components (tn-table's [@detailExpand]);
// all provide*Animations* APIs are deprecated in Angular 20.2+. Revisit once the library
// moves to animate.enter/leave.
// eslint-disable-next-line sonarjs/deprecation
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import {
  withPreloading,
  provideRouter,
  PreloadAllModules,
  Router,
  NavigationEnd,
  withComponentInputBinding,
  withNavigationErrorHandler,
  NavigationError,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import {
  TranslateModule, TranslateLoader, TranslateCompiler, MissingTranslationHandler,
} from '@ngx-translate/core';
import { TN_TEST_ATTR, TnSpriteLoaderService } from '@truenas/ui-components';
import { environment } from 'environments/environment';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideMarkdown } from 'ngx-markdown';
import { NgxPopperjsModule } from 'ngx-popperjs';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { provideNgxWebstorage, withLocalStorage } from 'ngx-webstorage';
import { filter, take } from 'rxjs';
import { AppComponent } from 'app/app.component';
import { rootRoutes } from 'app/app.routes';
import { defaultLanguage } from 'app/constants/languages.constant';
import { provideTnAutocompleteLabels } from 'app/core/providers/tn-autocomplete-labels.provider';
import { provideTnCalendarIntl } from 'app/core/providers/tn-calendar-intl.provider';
import { provideTnChipInputLabels } from 'app/core/providers/tn-chip-input-labels.provider';
import { provideTnDialogLabels } from 'app/core/providers/tn-dialog-labels.provider';
import { provideTnFallbackLabels } from 'app/core/providers/tn-fallback-labels.provider';
import { provideTnFilePickerLabels } from 'app/core/providers/tn-file-picker-labels.provider';
import {
  provideTnFormFieldDismissibleErrors,
  provideTnFormFieldErrors,
} from 'app/core/providers/tn-form-field-errors.provider';
import { provideTnSelectLabels } from 'app/core/providers/tn-select-labels.provider';
import { provideTnTableLabels } from 'app/core/providers/tn-table-labels.provider';
import { provideTnTablePagerLabels } from 'app/core/providers/tn-table-pager-labels.provider';
import { chunkReloadKey, handleChunkLoadError } from 'app/helpers/handle-chunk-load-error';
import { WINDOW, getWindow } from 'app/helpers/window.helper';
import { IcuMissingTranslationHandler } from 'app/modules/language/translations/icu-missing-translation-handler';
import { createTranslateLoader } from 'app/modules/language/translations/icu-translations-loader';
import { ApiService } from 'app/modules/websocket/api.service';
import { ConnectionService } from 'app/modules/websocket/connection.service';
import { provideWebSocketDebugState } from 'app/modules/websocket-debug-panel/providers/websocket-debug.providers';
import { ErrorHandlerService } from 'app/services/errors/error-handler.service';
import { ServiceWorkerService } from 'app/services/service-worker.service';
import { rootReducers, rootEffects } from 'app/store';
import { CustomRouterStateSerializer } from 'app/store/router/custom-router-serializer';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    // No zone.js: change detection is scheduled by signals, `markForCheck()`, template/host
    // listeners and `async` pipe emissions. Every component is OnPush, which already required
    // all of those, so nothing relies on zone.js patching browser APIs to trigger a tick.
    provideZonelessChangeDetection(),
    // Without zone.js, errors thrown from timers, promises and event listeners outside Angular
    // never reach `ErrorHandler` on their own; this forwards window `error` and
    // `unhandledrejection` events to it.
    provideBrowserGlobalErrorListeners(),
    importProvidersFrom(
      BrowserModule,
      TranslateModule.forRoot({
        defaultLanguage,
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: IcuMissingTranslationHandler,
        },
        useDefaultLang: false,
      }),
      NgxPopperjsModule.forRoot({ appendTo: 'body', hideOnScroll: true }),
      NgxSkeletonLoaderModule.forRoot({
        theme: {
          'background-color': 'var(--alt-bg2)',
          opacity: 0.25,
        },
      }),
    ),
    provideMarkdown({ loader: HttpClient }),
    provideStore(rootReducers, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: true,
        strictActionSerializability: true,
        // The app runs without zone.js, so there is no zone for actions to be dispatched in.
        strictActionWithinNgZone: false,
        strictActionTypeUniqueness: true,
      },
    }),
    provideEffects(rootEffects),
    ...(environment.debugPanel?.enabled ? [provideWebSocketDebugState()] : []),
    provideRouterStore({
      serializer: CustomRouterStateSerializer,
    }),
    provideNgxWebstorage(withLocalStorage()),
    // Registers a no-op animation engine so synthetic animation bindings (e.g. tn-table's
    // [@detailExpand] detail row) resolve without errors, while keeping the app's
    // long-standing instant (non-animated) behavior unchanged.
    // eslint-disable-next-line sonarjs/deprecation -- see import note above.
    provideNoopAnimations(),
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: WINDOW,
      useFactory: getWindow,
    },
    {
      // webui targets `data-test` (thousands of existing selectors), so switch the
      // ui-components library off its `data-testid` default. This one provider routes every
      // component-level `testId` input and `TnTestIdDirective` binding through `data-test`,
      // so the existing automated selectors keep matching.
      provide: TN_TEST_ATTR,
      useValue: 'data-test',
    },
    provideTnTablePagerLabels(),
    provideTnFormFieldErrors(),
    provideTnFormFieldDismissibleErrors(),
    provideTnCalendarIntl(),
    provideTnSelectLabels(),
    provideTnAutocompleteLabels(),
    provideTnChipInputLabels(),
    provideTnDialogLabels(),
    provideTnTableLabels(),
    provideTnFilePickerLabels(),
    provideTnFallbackLabels(),
    provideAppInitializer(() => {
      const swService = inject(ServiceWorkerService);
      swService.register();
    }),
    provideAppInitializer(() => {
      // Opens the tab's socket at startup, so its session is already
      // authenticated when the first typed consumer appears. Unconditional on
      // purpose: migrated pages run entirely on this socket, so there is no
      // build in which it can be left closed.
      inject(ApiService);
      // Publishes the socket's state from the first frame, before anything
      // that reads `WebSocketStatusService.isConnected$` has been created.
      inject(ConnectionService);
    }),
    provideAppInitializer(() => {
      const spriteLoader = inject(TnSpriteLoaderService);
      return spriteLoader.ensureSpriteLoaded();
    }),
    provideAppInitializer(() => {
      const router = inject(Router);
      const windowRef = inject<Window>(WINDOW);
      router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      ).subscribe(() => {
        try {
          windowRef.sessionStorage.removeItem(chunkReloadKey);
        } catch { /* sessionStorage may be unavailable */ }
      });
    }),
    provideCharts(withDefaultRegisterables()),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideRouter(
      rootRoutes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withNavigationErrorHandler((error: NavigationError) => {
        handleChunkLoadError(error, inject<Window>(WINDOW));
      }),
    ),
  ],
});
